<script setup>
import { computed, ref, useAttrs } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import {
  isVoiceCallEnabled,
  getBridgeInboxType,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import { useAlert } from 'dashboard/composables';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { useBuzzerBridgeSip } from 'dashboard/composables/useBuzzerBridgeSip';
import { BUZZER_BRIDGE_REGISTRATION_STATUS } from 'dashboard/services/BuzzerBridgeSipClient';
import BuzzerBridgeCallsAPI from 'dashboard/api/channel/voice/buzzerBridgeCallsAPI';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  phone: { type: String, default: '' },
  contactId: { type: [String, Number], required: true },
  // When set and the conversation's inbox can place calls, the call goes out
  // through that inbox; otherwise the agent picks a voice inbox.
  conversationId: { type: [String, Number], default: null },
  label: { type: String, default: '' },
  icon: { type: [String, Object, Function], default: '' },
  size: { type: String, default: 'sm' },
  tooltipLabel: { type: String, default: '' },
});

defineOptions({ inheritAttrs: false });
const attrs = useAttrs();
const route = useRoute();
const router = useRouter();
const store = useStore();

const { t } = useI18n();

const dialogRef = ref(null);

const inboxesList = useMapGetter('inboxes/getInboxes');
const currentUser = useMapGetter('getCurrentUser');
const bridgeSession = useBuzzerBridgeSip();
const isInitiatingCall = ref(false);

// Calls go through Buzzer Bridge, so an inbox is only dialable by agents
// provisioned with SIP credentials.
const canUseInbox = inbox =>
  isVoiceCallEnabled(inbox) && !!currentUser.value?.voice_calls_enabled;

const voiceInboxes = computed(() =>
  (inboxesList.value || []).filter(canUseInbox)
);

const conversationInbox = computed(() => {
  if (!props.conversationId) return null;
  const conversation = store.getters.getConversationById(props.conversationId);
  const inbox = (inboxesList.value || []).find(
    i => i.id === conversation?.inbox_id
  );
  return inbox && canUseInbox(inbox) ? inbox : null;
});

const shouldRender = computed(
  () =>
    (voiceInboxes.value.length > 0 || conversationInbox.value) && !!props.phone
);

// Block a new call whenever any provider call is already active or ringing,
// otherwise starting one here would leave a still-live session with no control.
const isCallButtonDisabled = computed(
  () =>
    store.getters['calls/hasActiveCall'] ||
    store.getters['calls/hasIncomingCall'] ||
    isInitiatingCall.value
);

const navigateToConversation = conversationId => {
  const accountId = route.params.accountId;
  if (conversationId && accountId) {
    const path = frontendURL(
      conversationUrl({
        accountId,
        id: conversationId,
      })
    );
    router.push({ path });
  }
};

const startCall = async (inbox, conversationIdHint = null) => {
  if (isCallButtonDisabled.value) return;
  if (
    bridgeSession.registrationStatus.value !==
    BUZZER_BRIDGE_REGISTRATION_STATUS.REGISTERED
  ) {
    useAlert(t('CONTACT_PANEL.BRIDGE_NOT_CONNECTED'));
    return;
  }

  const clientRef = window.crypto.randomUUID();
  let call;
  isInitiatingCall.value = true;
  try {
    call = await BuzzerBridgeCallsAPI.initiate({
      conversationId: conversationIdHint,
      contactId: props.contactId,
      inboxId: inbox.id,
      clientRef,
    });
    store.dispatch('calls/addCall', {
      callSid: call.call_sid,
      callId: call.id,
      conversationId: call.conversation_id,
      inboxId: call.inbox_id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.BRIDGE,
      caller: call.caller,
    });

    const locale = currentUser.value?.ui_settings?.locale;
    await bridgeSession.dial(props.phone, {
      account_id: Number(route.params.accountId),
      inbox_id: inbox.id,
      inbox_type: getBridgeInboxType(inbox),
      conversation_id: call.conversation_id,
      agent_email: currentUser.value?.email,
      lang: ['he', 'en', 'ru'].includes(locale) ? locale : undefined,
      client_ref: clientRef,
    });
    navigateToConversation(call.conversation_id);
    useAlert(t('CONTACT_PANEL.CALL_INITIATED'));
  } catch (error) {
    if (call?.id) {
      BuzzerBridgeCallsAPI.report(call.id, {
        status: 'failed',
        endReason: 'failed',
      }).catch(() => {});
      store.dispatch('calls/dismissCall', call.call_sid);
    }
    useAlert(
      error?.response?.data?.error ||
        error?.message ||
        t('CONTACT_PANEL.CALL_FAILED')
    );
  } finally {
    isInitiatingCall.value = false;
  }
};

const onClick = async () => {
  if (conversationInbox.value) {
    await startCall(conversationInbox.value, props.conversationId);
    return;
  }
  if (voiceInboxes.value.length > 1) {
    dialogRef.value?.open();
    return;
  }
  const [inbox] = voiceInboxes.value;
  await startCall(inbox);
};

const onPickInbox = async inbox => {
  dialogRef.value?.close();
  await startCall(inbox);
};
</script>

<template>
  <span class="contents">
    <Button
      v-if="shouldRender"
      v-tooltip.top-end="tooltipLabel || null"
      v-bind="attrs"
      :disabled="isCallButtonDisabled"
      :is-loading="isInitiatingCall"
      :label="label"
      :icon="icon"
      :size="size"
      @click="onClick"
    />

    <Dialog
      v-if="shouldRender && voiceInboxes.length > 1"
      ref="dialogRef"
      :title="$t('CONTACT_PANEL.VOICE_INBOX_PICKER.TITLE')"
      show-cancel-button
      :show-confirm-button="false"
      width="md"
    >
      <div class="flex flex-col gap-2">
        <button
          v-for="inbox in voiceInboxes"
          :key="inbox.id"
          type="button"
          class="flex items-center justify-between w-full px-4 py-2 text-left rounded-lg hover:bg-n-alpha-2"
          @click="onPickInbox(inbox)"
        >
          <div class="flex items-center gap-2">
            <span class="i-ri-phone-fill text-n-slate-10" />
            <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
          </div>
          <span v-if="inbox.phone_number" class="text-xs text-n-slate-10">
            {{ inbox.phone_number }}
          </span>
        </button>
      </div>
    </Dialog>
  </span>
</template>
